/**
 * Shared cell renderers for the invoice list table. Mirrors the legacy
 * `getInvoiceListColumns` intent (invoice-code link, customer id-name link,
 * service link / deposit label, status pill, type/sale-type chips,
 * right-aligned money + dates) on `@vnetwork/ui` primitives + `--vn-*` tokens.
 * No hand-rolled pills/chips.
 */
import type { ReactNode } from 'react';
import { Chip } from '@vnetwork/ui/primitives';
import { StatusPill } from '@vnetwork/ui/data';
import { TableLink } from '@/components/table-link';
import { EntityLink } from '@/components/entity-link';
import { formatDate, formatMoney } from '@/lib/format';
import type { InvoiceRow } from '@/api/invoices';
import {
  invoiceStatusTone,
  invoiceStatusLabel,
  invoiceTypeLabel,
  invoiceSaleTypeLabel,
} from '../invoice-format';

const muted = { color: 'var(--vn-fg-muted)', fontStyle: 'italic' } as const;

/** Em-dash placeholder for a missing value. */
function emDash(): ReactNode {
  return <span style={muted}>—</span>;
}

/**
 * Invoice No. → the plain `invoice_id`, linked to the detail route. Legacy shows
 * `invoice_id` here (NOT the `YYYYMMDD-nnnn` `invoice_code`, which is a separate
 * system code).
 */
export function invoiceCodeCell(row: InvoiceRow): ReactNode {
  return (
    <TableLink to={`/invoices/${row.invoice_id}`} title={String(row.invoice_id)}>
      {row.invoice_id}
    </TableLink>
  );
}

/**
 * Customer: single-line `{id} – {name}`, linked to the customer detail.
 * Falls back to an em-dash when the invoice has no embedded customer.
 */
export function invoiceCustomerCell(row: InvoiceRow): ReactNode {
  const customer = row.customer;
  if (!customer) return emDash();
  return (
    <EntityLink
      to={`/customers/${customer.customer_id}`}
      id={customer.customer_id}
      name={customer.company_name || customer.customer_name}
    />
  );
}

/**
 * Service: single-line `{id} – {name}`, linked. Deposit invoices show the
 * "Deposit" label (matching legacy); missing service → dash.
 */
export function invoiceServiceCell(row: InvoiceRow): ReactNode {
  if (row.invoice_type === 'deposit') {
    return <span>Deposit</span>;
  }
  const svc = row.service;
  if (!svc || !svc.service_id) return emDash();
  return (
    <EntityLink
      to={`/services/${svc.service_id}`}
      id={svc.service_id}
      name={svc.service_name}
    />
  );
}

/** Status → `StatusPill` with the shared invoice tone map. */
export function invoiceStatusCell(status?: string): ReactNode {
  return <StatusPill tone={invoiceStatusTone(status)}>{invoiceStatusLabel(status)}</StatusPill>;
}

/** Invoice type as a neutral `Chip`. */
export function invoiceTypeCell(type?: string): ReactNode {
  if (!type) return emDash();
  return <Chip tone="neutral">{invoiceTypeLabel(type)}</Chip>;
}

/** Sale type as a tinted `Chip` (new sell / upsell / renewable). */
export function invoiceSaleTypeCell(saleType?: string): ReactNode {
  if (!saleType) return emDash();
  const tone = saleType === 'new-sell' ? 'info' : saleType === 'upsell' ? 'success' : 'neutral';
  return <Chip tone={tone}>{invoiceSaleTypeLabel(saleType)}</Chip>;
}

/** Account managers → comma-joined names from the embedded customer. */
export function invoiceAccountManagersCell(row: InvoiceRow): ReactNode {
  const names = row.customer?.account_manager_names?.filter(Boolean) ?? [];
  if (names.length === 0) return emDash();
  return <span style={{ whiteSpace: 'nowrap' }}>{names.join(', ')}</span>;
}

/** Exporter code (VAT export reference) — muted mono-ish text, dash when unset. */
export function invoiceExporterCodeCell(row: InvoiceRow): ReactNode {
  return row.invoice_exporter_code ? (
    <span style={{ whiteSpace: 'nowrap' }}>{row.invoice_exporter_code}</span>
  ) : (
    emDash()
  );
}

/** Sent-email count ("N×"), right-aligned; dash when never sent. */
export function invoiceMailCountCell(row: InvoiceRow): ReactNode {
  const n = row.invoice_mail_count ?? 0;
  return (
    <span style={{ display: 'block', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
      {n > 0 ? `${n}×` : emDash()}
    </span>
  );
}

/** Right-aligned VND money cell. */
export function moneyCell(value: number | null | undefined): ReactNode {
  return (
    <span style={{ display: 'block', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
      {formatMoney(value)}
    </span>
  );
}

/** Right-aligned DD/MM/YYYY date cell. */
export function dateCell(iso?: string | null): ReactNode {
  return (
    <span style={{ display: 'block', textAlign: 'right', whiteSpace: 'nowrap' }}>
      {formatDate(iso)}
    </span>
  );
}

/** Right-aligned DD/MM/YYYY – DD/MM/YYYY applied-period range cell. */
export function appliedRangeCell(start?: string | null, end?: string | null): ReactNode {
  if (!start && !end) return emDash();
  return (
    <span
      style={{ display: 'block', textAlign: 'right', whiteSpace: 'nowrap' }}
      title={`${formatDate(start)} – ${formatDate(end)}`}
    >
      {formatDate(start)} – {formatDate(end)}
    </span>
  );
}
